import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import AssessmentIcon from "@material-ui/icons/Assessment";
import TimelineIcon from "@material-ui/icons/Timeline";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AndroidIcon from "@material-ui/icons/Android";
import ProductSelector from "./ProductSelector";
import ProductAnalyticsDisplay from "./ProductAnalyticsDisplay";
import ProductPerformanceCharts from "./ProductPerformanceCharts";
import ProductScoringPanel from "./ProductScoringPanel";
import ProductProjections from "./ProductProjections";
import SalesAgentPanel from "./SalesAgentPanel";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
});

const hebrewLabels = {
  title: "אנליטיקה מתקדמת של מוצרים",
  tabs: {
    analytics: "אנליטיקה",
    scoring: "ניקוד וביצועים",
    projections: "תחזיות",
    agent: "סוכן AI",
  },
  emptyState: {
    selectProduct: "בחר מוצר כדי להציג אנליטיקה מפורטת",
    noData: "אין נתונים זמינים",
  },
};

function TabPanel(props) {
  const { children, value, index, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-analytics-tabpanel-${index}`}
      aria-labelledby={`product-analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className={classes.tabPanel}>{children}</Box>
      )}
    </div>
  );
}

class ProductAnalyticsPage extends React.Component {
  state = {
    selectedProduct: null,
    activeTab: 0,
    productsLoaded: false,
  };

  componentDidMount() {
    // Read initial state from URL
    this.syncFromURL();
  }

  componentDidUpdate(prevProps) {
    // Handle browser back/forward navigation
    if (prevProps.location.search !== this.props.location.search) {
      this.syncFromURL();
    }
  }

  syncFromURL = () => {
    const searchParams = new URLSearchParams(this.props.location.search);
    const ean = searchParams.get('ean');
    const tab = searchParams.get('tab');

    // Parse tab index (default to 0 if invalid)
    const tabIndex = tab ? parseInt(tab, 10) : 0;
    const validTabIndex = tabIndex >= 0 && tabIndex <= 3 ? tabIndex : 0;

    // Update state if different
    if (this.state.activeTab !== validTabIndex) {
      this.setState({ activeTab: validTabIndex });
    }

    // If there's an EAN in URL but no product selected yet, we need to load it
    if (ean && (!this.state.selectedProduct || this.state.selectedProduct.ean !== ean)) {
      this.loadProductByEAN(ean);
    }
  };

  loadProductByEAN = async (ean) => {
    try {
      const basePath = utils.getEdgeBasePath();
      const response = await fetch(`${basePath}/api/v1/admin/products/list`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();

      if (data.ok && data.products) {
        const product = data.products.find(p => p.ean === ean);
        if (product) {
          this.setState({ selectedProduct: product });
        }
      }
    } catch (error) {
      console.error('Error loading product by EAN:', error);
    }
  };

  updateURL = (product, tabIndex) => {
    const searchParams = new URLSearchParams();
    
    if (product && product.ean) {
      searchParams.set('ean', product.ean);
    }
    
    if (tabIndex !== 0) {
      searchParams.set('tab', tabIndex.toString());
    }

    const newSearch = searchParams.toString();
    const newURL = newSearch ? `?${newSearch}` : this.props.location.pathname;

    // Only update if the URL actually changed
    if (`?${newSearch}` !== this.props.location.search) {
      this.props.history.push(newURL);
    }
  };

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue }, () => {
      this.updateURL(this.state.selectedProduct, newValue);
    });
  };

  handleProductSelect = (product) => {
    this.setState({ selectedProduct: product, activeTab: 0 }, () => {
      // Reset to first tab when selecting a new product
      this.updateURL(product, 0);
    });
  };

  render() {
    const { classes } = this.props;
    const { selectedProduct, activeTab } = this.state;

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Typography
            variant="h6"
            gutterBottom
            style={{ marginBottom: 16, fontWeight: 500 }}
          >
            {hebrewLabels.emptyState.selectProduct}
          </Typography>
          <ProductSelector
            value={selectedProduct}
            onChange={this.handleProductSelect}
          />
        </Paper>

        {selectedProduct && (
          <>
            <Paper className={classes.paper}>
              <Tabs
                value={activeTab}
                onChange={this.handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                className={classes.tabs}
              >
                <Tab
                  icon={<AssessmentIcon />}
                  label={hebrewLabels.tabs.analytics}
                />
                <Tab
                  icon={<TrendingUpIcon />}
                  label={hebrewLabels.tabs.scoring}
                />
                <Tab
                  icon={<TimelineIcon />}
                  label={hebrewLabels.tabs.projections}
                />
                <Tab
                  icon={<AndroidIcon />}
                  label={hebrewLabels.tabs.agent}
                />
              </Tabs>

              <TabPanel value={activeTab} index={0} classes={classes}>
                <ProductAnalyticsDisplay product={selectedProduct} />
                <ProductPerformanceCharts product={selectedProduct} />
              </TabPanel>

              <TabPanel value={activeTab} index={1} classes={classes}>
                <ProductScoringPanel product={selectedProduct} />
              </TabPanel>

              <TabPanel value={activeTab} index={2} classes={classes}>
                <ProductProjections product={selectedProduct} />
              </TabPanel>

              <TabPanel value={activeTab} index={3} classes={classes}>
                <SalesAgentPanel />
              </TabPanel>
            </Paper>
          </>
        )}
      </div>
    );
  }
}

ProductAnalyticsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(ProductAnalyticsPage));
